import { HomeFixture } from 'services/freeform';

import type { CardFixtureProps } from 'core/generic/components/cards/card-fixture';

const FINISHED_PERIODS = new Set([
	'FullTime',
	'ExtraTime',
	'PenaltyShootout',
	'PostMatch',
]);

const parseScore = (score: unknown): number | undefined => {
	if (typeof score === 'number' && Number.isFinite(score)) {
		return score;
	}

	if (typeof score === 'string' && score.trim() !== '') {
		const parsed = Number(score);
		if (Number.isFinite(parsed)) {
			return parsed;
		}
	}

	return undefined;
};

export const mapFixtureToCardProps = (fixture: HomeFixture) => {
	const kickOff =
		fixture.kickoff?.adjustedKickOff?.kickOff || fixture.kickoff?.kickOff;

	const dateLabel = kickOff
		? new Date(kickOff).toLocaleDateString(undefined, {
				weekday: 'short',
				day: '2-digit',
				month: 'short',
			})
		: undefined;

	const kickOffLabel =
		kickOff && !fixture.kickoff?.hideTbcTime
			? new Date(kickOff).toLocaleTimeString(undefined, {
					hour: '2-digit',
					minute: '2-digit',
				})
			: 'TBC';

	const homeScore = parseScore(fixture.homeTeam?.score);
	const awayScore = parseScore(fixture.awayTeam?.score);
	const period = fixture.period;
	const isResult =
		(period != null && FINISHED_PERIODS.has(period)) ||
		(homeScore !== undefined &&
			awayScore !== undefined &&
			period !== 'PreMatch' &&
			period !== 'FirstHalf' &&
			period !== 'SecondHalf' &&
			period !== 'HalfTime');

	const previewHref = fixture.webPageUrl || fixture.matchDayCentreUrl;

	return {
		variant: isResult ? 'result' : 'next-match',
		showAccent: !isResult,
		title: isResult ? 'Last result' : 'Next fixture',
		viewAllHref: '/fixtures/',
		viewAllLabel: isResult ? 'All results' : 'All fixtures',
		dateLabel,
		dateTime: kickOff,
		competitionName: fixture.competition?.name,
		homeTeam: {
			name:
				fixture.homeTeam?.shortName || fixture.homeTeam?.name || 'Home',
			crestUrl: fixture.homeTeam?.crest?.url,
			crestAlt:
				fixture.homeTeam?.crest?.alt ||
				fixture.homeTeam?.crest?.altText ||
				fixture.homeTeam?.name,
		},
		awayTeam: {
			name:
				fixture.awayTeam?.shortName || fixture.awayTeam?.name || 'Away',
			crestUrl: fixture.awayTeam?.crest?.url,
			crestAlt:
				fixture.awayTeam?.crest?.alt ||
				fixture.awayTeam?.crest?.altText ||
				fixture.awayTeam?.name,
		},
		kickOffLabel,
		timeZone: fixture.kickoff?.adjustedKickOff?.timezone,
		homeScore,
		awayScore,
		showCrests: true,
		secondaryCtaHref: isResult ? undefined : previewHref,
		secondaryCtaLabel: 'Preview',
		secondaryCtaVariant: 'tertiary',
		ctaHref: isResult ? previewHref : fixture.tickets?.buyTicketsLink,
		ctaLabel: isResult
			? 'Match report'
			: fixture.tickets?.buyTicketsLinkText || 'Buy tickets',
		ctaVariant: isResult ? 'secondary' : 'featured',
	} satisfies CardFixtureProps;
};
